import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useFeatures } from '../context/FeatureContext';
import authService from '../services/authService';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendStatus, setResendStatus] = useState('');

  const { login, loginWithGoogle } = useAuth();
  const { googleSsoEnabled } = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/events';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.user.profile_completed === false) {
        navigate('/settings?setup=1', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      const apiError = err.response?.data?.error;
      setNeedsVerification(apiError?.data?.code === 'EMAIL_NOT_VERIFIED');
      setResendStatus('');
      setError(apiError?.message || err.message || 'Failed to login. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendStatus('sending');
    try {
      await authService.resendVerification(email);
      setResendStatus('sent');
    } catch {
      setResendStatus('failed');
    }
  };

  return (
    <div className="auth-container">
      <style>{`
        .auth-container {
          min-height: 100vh;
          display: grid;
          grid-template-columns: 1fr 1fr;
          font-family: 'DM Sans', system-ui, sans-serif;
          background: #faf8f5;
        }

        @media (max-width: 968px) {
          .auth-container {
            grid-template-columns: 1fr;
          }
          .auth-visual {
            display: none;
          }
        }

        /* Visual Panel - Education Theme */
        .auth-visual {
          background: linear-gradient(160deg, #0c4a6e 0%, #164e63 40%, #1e3a5f 100%);
          position: relative;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          padding: 2.5rem;
        }

        /* Subtle pattern overlay - notebook grid */
        .auth-visual::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px);
          background-size: 24px 24px;
          z-index: 0;
        }

        /* Warm glow accents */
        .auth-visual::after {
          content: '';
          position: absolute;
          top: -20%;
          right: -20%;
          width: 60%;
          height: 60%;
          background: radial-gradient(circle, rgba(251, 191, 36, 0.15) 0%, transparent 60%);
          z-index: 0;
        }

        .visual-glow-bottom {
          position: absolute;
          bottom: -10%;
          left: -10%;
          width: 50%;
          height: 50%;
          background: radial-gradient(circle, rgba(56, 189, 248, 0.1) 0%, transparent 60%);
          z-index: 0;
        }

        .visual-content {
          position: relative;
          z-index: 1;
        }

        .visual-logo {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .visual-logo-icon {
          width: 44px;
          height: 44px;
          background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(251, 191, 36, 0.3);
        }

        .visual-logo-icon svg {
          width: 24px;
          height: 24px;
          color: #1e3a5f;
        }

        .visual-logo-text {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.5rem;
          color: #fff;
          font-weight: 400;
        }

        .visual-main {
          position: relative;
          z-index: 1;
          flex: 1;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 2rem 0;
        }

        .visual-badge {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(251, 191, 36, 0.15);
          border: 1px solid rgba(251, 191, 36, 0.3);
          padding: 0.5rem 1rem;
          border-radius: 100px;
          color: #fcd34d;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          margin-bottom: 1.5rem;
          width: fit-content;
        }

        .visual-badge svg {
          width: 14px;
          height: 14px;
        }

        .visual-tagline {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: clamp(2rem, 4vw, 2.75rem);
          color: #fff;
          line-height: 1.25;
          max-width: 380px;
          margin-bottom: 1.5rem;
        }

        .visual-tagline em {
          color: #fbbf24;
          font-style: italic;
        }

        .visual-description {
          color: rgba(255, 255, 255, 0.7);
          font-size: 1rem;
          line-height: 1.6;
          max-width: 340px;
        }

        /* Feature highlights */
        .visual-features {
          position: relative;
          z-index: 1;
          display: flex;
          flex-direction: column;
          gap: 1rem;
          margin-top: 2rem;
        }

        .visual-feature {
          display: flex;
          align-items: center;
          gap: 0.875rem;
          color: rgba(255, 255, 255, 0.8);
          font-size: 0.9rem;
        }

        .visual-feature-icon {
          width: 36px;
          height: 36px;
          background: rgba(255, 255, 255, 0.1);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .visual-feature-icon svg {
          width: 18px;
          height: 18px;
          color: #fbbf24;
        }

        .visual-footer {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          color: rgba(255,255,255,0.5);
          font-size: 0.8125rem;
        }

        .visual-footer svg {
          width: 16px;
          height: 16px;
          color: #fbbf24;
        }

        /* Form Section */
        .auth-form-section {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
          background: linear-gradient(180deg, #faf8f5 0%, #f5f3f0 100%);
        }

        .auth-form-wrapper {
          width: 100%;
          max-width: 420px;
          animation: slideUp 0.6s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .mobile-header {
          display: none;
          text-align: center;
          margin-bottom: 2rem;
        }

        @media (max-width: 968px) {
          .mobile-header {
            display: block;
          }
        }

        .mobile-logo {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .mobile-logo-icon {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .mobile-logo-icon svg {
          width: 20px;
          height: 20px;
          color: #fbbf24;
        }

        .mobile-logo-text {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.25rem;
          color: #1e3a5f;
        }

        .mobile-tagline {
          color: #64748b;
          font-size: 0.875rem;
        }

        .form-card {
          background: #fff;
          border-radius: 20px;
          padding: 2.5rem;
          box-shadow:
            0 1px 3px rgba(0, 0, 0, 0.04),
            0 8px 24px rgba(0, 0, 0, 0.06);
        }

        .form-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .form-header h1 {
          font-family: 'Instrument Serif', Georgia, serif;
          font-size: 1.875rem;
          font-weight: 400;
          color: #1e3a5f;
          margin: 0 0 0.5rem 0;
        }

        .form-header p {
          color: #64748b;
          margin: 0;
          font-size: 0.9375rem;
        }

        .form-header p a {
          color: #0c4a6e;
          text-decoration: none;
          font-weight: 600;
          transition: color 0.2s;
        }

        .form-header p a:hover {
          color: #0369a1;
          text-decoration: underline;
        }

        .auth-form {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 600;
          color: #374151;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .form-label a {
          color: #0c4a6e;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.8125rem;
          transition: color 0.2s;
        }

        .form-label a:hover {
          color: #0369a1;
          text-decoration: underline;
        }

        .input-wrapper {
          position: relative;
        }

        .form-input {
          width: 100%;
          padding: 0.875rem 1rem;
          font-size: 1rem;
          font-family: inherit;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          background: #fff;
          color: #1e3a5f;
          transition: all 0.2s ease;
          box-sizing: border-box;
        }

        .form-input:hover {
          border-color: #cbd5e1;
        }

        .form-input:focus {
          outline: none;
          border-color: #0c4a6e;
          box-shadow: 0 0 0 4px rgba(12, 74, 110, 0.1);
        }

        .form-input::placeholder {
          color: #94a3b8;
        }

        .form-input.has-error {
          border-color: #ef4444;
        }

        .password-toggle {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          color: #64748b;
          cursor: pointer;
          padding: 0.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
          border-radius: 6px;
        }

        .password-toggle:hover {
          color: #1e3a5f;
          background: #f1f5f9;
        }

        .error-message {
          background: #fef2f2;
          border: 1px solid #fecaca;
          color: #dc2626;
          padding: 0.875rem 1rem;
          border-radius: 12px;
          font-size: 0.875rem;
          display: flex;
          align-items: flex-start;
          gap: 0.625rem;
          animation: shake 0.4s ease;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }

        .error-icon {
          flex-shrink: 0;
          margin-top: 1px;
        }

        .submit-btn {
          background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
          color: #fff;
          border: none;
          padding: 1rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          font-family: inherit;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          margin-top: 0.5rem;
          box-shadow: 0 2px 8px rgba(12, 74, 110, 0.25);
        }

        .submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #0e5a85 0%, #1a6478 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(12, 74, 110, 0.35);
        }

        .submit-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255,255,255,0.3);
          border-top-color: #fff;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .form-footer {
          text-align: center;
          margin-top: 1.5rem;
          padding-top: 1.5rem;
          border-top: 1px solid #e2e8f0;
        }

        .form-footer p {
          color: #64748b;
          font-size: 0.8125rem;
          margin: 0;
        }

        .form-footer a {
          color: #0c4a6e;
          font-weight: 600;
          text-decoration: none;
        }

        .form-footer a:hover {
          text-decoration: underline;
        }

        .legal-links {
          margin-top: 0.75rem;
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.8125rem;
        }

        .legal-links a {
          color: #64748b;
          font-weight: 500;
          text-decoration: none;
          transition: color 0.2s;
        }

        .legal-links a:hover {
          color: #0c4a6e;
          text-decoration: underline;
        }

        .legal-separator {
          color: #cbd5e1;
          font-size: 0.75rem;
        }

        /* Trust badges */
        .trust-section {
          margin-top: 2rem;
          text-align: center;
        }

        .trust-text {
          color: #94a3b8;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          margin-bottom: 0.75rem;
        }

        .trust-badges {
          display: flex;
          justify-content: center;
          gap: 1.5rem;
          color: #64748b;
        }

        .trust-badge {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          font-size: 0.8125rem;
        }

        .trust-badge svg {
          width: 16px;
          height: 16px;
          color: #22c55e;
        }

        .google-hint {
          text-align: center;
          color: #94a3b8;
          font-size: 0.8125rem;
          margin-bottom: 0.75rem;
        }

        .google-btn {
          width: 100%;
          background: linear-gradient(135deg, #0c4a6e 0%, #164e63 100%);
          color: #fff;
          border: none;
          padding: 0.75rem 1.5rem;
          font-size: 1rem;
          font-weight: 600;
          font-family: inherit;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          box-shadow: 0 2px 8px rgba(12, 74, 110, 0.25);
        }

        .google-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #0e5a85 0%, #1a6478 100%);
          transform: translateY(-1px);
          box-shadow: 0 4px 16px rgba(12, 74, 110, 0.35);
        }

        .google-btn:active:not(:disabled) {
          transform: translateY(0);
        }

        .google-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .google-btn svg {
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .email-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 1rem;
          margin: 1.25rem 0 0;
          color: #94a3b8;
          font-size: 0.8125rem;
        }

        .email-toggle::before,
        .email-toggle::after {
          content: '';
          flex: 1;
          height: 1px;
          background: #e2e8f0;
        }

        .email-toggle-link {
          background: none;
          border: none;
          padding: 0;
          color: #64748b;
          font-size: 0.8125rem;
          font-family: inherit;
          cursor: pointer;
          transition: color 0.2s;
          white-space: nowrap;
        }

        .email-toggle-link:hover {
          color: #0c4a6e;
        }
      `}</style>

      <div className="auth-visual">
        <div className="visual-glow-bottom" />

        <div className="visual-content">
          <div className="visual-logo">
            <div className="visual-logo-icon">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <span className="visual-logo-text">rSchdlr</span>
          </div>
        </div>

        <div className="visual-main">
          <div className="visual-badge">
            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
            Professional Development
          </div>
          <h2 className="visual-tagline">
            Empowering <em>Educators</em>, Inspiring Growth
          </h2>
          <p className="visual-description">
            Connect with fellow educators, discover workshops, and track your professional learning journey all in one place.
          </p>

          <div className="visual-features">
            <div className="visual-feature">
              <div className="visual-feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <span>Collaborate with educators district-wide</span>
            </div>
            <div className="visual-feature">
              <div className="visual-feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
              <span>Build your personalized PD schedule</span>
            </div>
            <div className="visual-feature">
              <div className="visual-feature-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <span>Track credits and certifications</span>
            </div>
          </div>
        </div>

        <div className="visual-footer">
          <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          Trusted by school districts nationwide
        </div>
      </div>

      <div className="auth-form-section">
        <div className="auth-form-wrapper">
          <div className="mobile-header">
            <div className="mobile-logo">
              <div className="mobile-logo-icon">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
              <span className="mobile-logo-text">rSchdlr</span>
            </div>
            <p className="mobile-tagline">Professional Development Made Simple</p>
          </div>

          <div className="form-card">
            <div className="form-header">
              <h1>Welcome back</h1>
              <p>
                New to rSchdlr?{' '}
                <Link to="/register">Create an account</Link>
              </p>
            </div>

            {googleSsoEnabled && (
              <>
                <p className="google-hint">Most users sign in with their school Google account</p>
                <button
                  type="button"
                  className="google-btn"
                  disabled={googleLoading}
                  onClick={async () => {
                    setGoogleLoading(true);
                    try {
                      await loginWithGoogle(from);
                    } catch {
                      setGoogleLoading(false);
                    }
                  }}
                >
                  {googleLoading ? (
                    'Redirecting...'
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#fff"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#fff"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#fff"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#fff"/>
                      </svg>
                      Sign in with Google
                    </>
                  )}
                </button>
                {!showEmailForm && (
                  <div className="email-toggle">
                    <button
                      type="button"
                      className="email-toggle-link"
                      onClick={() => setShowEmailForm(true)}
                    >
                      Use email and password instead &rarr;
                    </button>
                  </div>
                )}
              </>
            )}

            {(!googleSsoEnabled || showEmailForm) && (
            <>
            {googleSsoEnabled && (
              <div className="email-toggle">or sign in with email instead</div>
            )}
            <form className="auth-form" onSubmit={handleSubmit}>
              {error && (
                <div className="error-message">
                  <svg className="error-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M8 1a7 7 0 100 14A7 7 0 008 1zM7 4.5a1 1 0 112 0v3a1 1 0 11-2 0v-3zm1 7a1 1 0 100-2 1 1 0 000 2z"/>
                  </svg>
                  <span>
                    {needsVerification
                      ? 'Your email address has not been verified yet. Check your inbox (and spam folder) for the verification link.'
                      : error}
                    {needsVerification && (
                      <>
                        {' '}
                        {resendStatus === 'sent' ? (
                          <strong>A new verification email has been sent.</strong>
                        ) : (
                          <button
                            type="button"
                            onClick={handleResendVerification}
                            disabled={resendStatus === 'sending'}
                            style={{ background: 'none', border: 'none', padding: 0, color: 'inherit', textDecoration: 'underline', cursor: 'pointer', font: 'inherit', fontWeight: 600 }}
                          >
                            {resendStatus === 'sending' ? 'Sending…' : 'Resend verification email'}
                          </button>
                        )}
                        {resendStatus === 'failed' && ' Could not send — please try again or contact the event organizer.'}
                      </>
                    )}
                  </span>
                </div>
              )}

              <div className="form-group">
                <label className="form-label" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  type="email"
                  className="form-input"
                  placeholder="you@school.edu"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
              </div>

              <div className="form-group">
                <label className="form-label" htmlFor="password">
                  Password
                  <Link to="/forgot-password">Forgot password?</Link>
                </label>
                <div className="input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    className="form-input"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    style={{ paddingRight: '3rem' }}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button type="submit" className="submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <div className="spinner" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </button>
            </form>
            </>
            )}

            <div className="form-footer">
              <p>
                Need help? <a href="mailto:support@rschdlr.com">Contact support</a>
              </p>
              <div className="legal-links">
                <Link to="/privacy">Privacy Policy</Link>
                <span className="legal-separator">|</span>
                <Link to="/terms">Terms of Service</Link>
              </div>
            </div>
          </div>

          <div className="trust-section">
            <div className="trust-text">Your data is protected</div>
            <div className="trust-badges">
              <div className="trust-badge">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Secure
              </div>
              <div className="trust-badge">
                <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                FERPA Compliant
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
